import { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api from "@/lib/api";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import {
  Gift, Plus, Copy, Check, Trash2, Pencil, Percent, DollarSign, CalendarPlus,
  Clock, BarChart3, Infinity as InfinityIcon,
} from "lucide-react";
import { cn } from "@/lib/utils";

const TYPES = [
  { key: "percent", label: "Descuento porcentual (%)", icon: Percent, hint: "Ej. 20 = 20% de descuento" },
  { key: "fixed", label: "Descuento en precio fijo", icon: DollarSign, hint: "Ej. 100 = -$100 MXN" },
  { key: "free_months", label: "Meses gratis", icon: CalendarPlus, hint: "Ej. 3 = 3 meses sin costo tras pagar" },
  { key: "bonus_months", label: "Meses bono al pagar anual", icon: Gift, hint: "Ej. 1 = pagas 12, recibes 13" },
  { key: "extended_trial", label: "Prueba extendida (días)", icon: Clock, hint: "Ej. 45 = trial de 45 días" },
  { key: "lifetime", label: "Lifetime (acceso extendido gratis)", icon: InfinityIcon, hint: "Otorga plan gratis por N meses, años o para siempre" },
];

const DURATION_UNITS = [
  { key: "months", label: "Meses" },
  { key: "years", label: "Años" },
  { key: "forever", label: "Para siempre" },
];

const typeMap = Object.fromEntries(TYPES.map((t) => [t.key, t]));

function fmtValue(promo) {
  const t = promo.type;
  if (t === "percent") return `${promo.value}% OFF`;
  if (t === "fixed") return `-$${promo.value} ${promo.currency || ""}`;
  if (t === "free_months") return `${promo.value} mes${promo.value === 1 ? "" : "es"} gratis`;
  if (t === "bonus_months") return `+${promo.value} mes${promo.value === 1 ? "" : "es"} bono`;
  if (t === "extended_trial") return `${promo.value} días de prueba`;
  if (t === "lifetime") {
    const u = promo.duration_unit || "months";
    if (u === "forever") return "Lifetime · para siempre";
    return `Lifetime · ${promo.value} ${u === "years" ? "año" + (promo.value === 1 ? "" : "s") : "mes" + (promo.value === 1 ? "" : "es")}`;
  }
  return String(promo.value);
}

function fmtDate(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString("es-MX", { day: "2-digit", month: "short", year: "numeric" }); }
  catch { return iso; }
}

const EMPTY = {
  code: "", name: "", description: "", type: "percent", value: 20,
  currency: "MXN", duration_unit: "months",
  valid_from: "", valid_until: "",
  max_uses: "", max_uses_per_user: 1, new_users_only: false,
  applicable_plans: [], is_active: true,
};

export default function AdminPromotions() {
  const qc = useQueryClient();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null); // promo or null (creating)
  const [copiedCode, setCopiedCode] = useState(null);

  const { data, isLoading } = useQuery({
    queryKey: ["admin-promotions"],
    queryFn: async () => (await api.get("/admin/promotions")).data,
  });
  const promos = data?.promotions || [];

  const del = useMutation({
    mutationFn: async (id) => (await api.delete(`/admin/promotions/${id}`)).data,
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["admin-promotions"] }); toast.success("Promoción eliminada"); },
    onError: (e) => toast.error(e?.response?.data?.detail || "No se pudo eliminar"),
  });

  const openCreate = () => { setEditing(null); setDialogOpen(true); };
  const openEdit = (p) => { setEditing(p); setDialogOpen(true); };

  const copy = (code) => {
    navigator.clipboard.writeText(code);
    setCopiedCode(code);
    setTimeout(() => setCopiedCode(null), 1500);
    toast.success(`Código ${code} copiado`);
  };

  return (
    <div className="p-6 md:p-10 space-y-6" data-testid="admin-promotions-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <p className="text-xs uppercase tracking-wider text-muted-foreground">Descuentos y promociones</p>
          <h1 className="font-display text-3xl font-semibold">Códigos promocionales</h1>
          <p className="text-sm text-muted-foreground mt-1">Crea cupones, descuentos y períodos de prueba extendida.</p>
        </div>
        <Button onClick={openCreate} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="promo-create-button">
          <Plus className="h-4 w-4 mr-2" /> Nueva promoción
        </Button>
      </div>

      {isLoading ? (
        <p className="text-sm text-muted-foreground">Cargando…</p>
      ) : promos.length === 0 ? (
        <div className="glass-panel rounded-2xl p-10 text-center">
          <Gift className="h-10 w-10 mx-auto text-blue-500 mb-2" />
          <p className="text-sm text-muted-foreground">Aún no tienes promociones. Crea la primera para arrancar campañas de conversión.</p>
        </div>
      ) : (
        <div className="grid gap-3" data-testid="promotions-list">
          {promos.map((p) => {
            const type = typeMap[p.type] || {};
            const Icon = type.icon || Gift;
            const usagePct = p.max_uses ? Math.min(100, Math.round((p.times_used / p.max_uses) * 100)) : null;
            return (
              <div
                key={p.promo_id}
                data-testid={`promo-${p.promo_id}`}
                className="glass-panel rounded-2xl p-4 flex items-center gap-4 flex-wrap"
              >
                <div className="h-12 w-12 rounded-xl grid place-items-center bg-blue-500/15 text-blue-600 dark:text-blue-300 shrink-0">
                  <Icon className="h-5 w-5" />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <button
                      onClick={() => copy(p.code)}
                      className="font-mono text-sm font-semibold px-2 py-0.5 rounded bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 inline-flex items-center gap-1"
                      data-testid={`promo-code-${p.promo_id}`}
                    >
                      {p.code}
                      {copiedCode === p.code ? <Check className="h-3 w-3 text-emerald-500" /> : <Copy className="h-3 w-3 opacity-60" />}
                    </button>
                    <Badge className={cn(
                      p.is_active ? "bg-emerald-500/15 text-emerald-700 dark:text-emerald-300"
                                  : "bg-slate-500/15 text-slate-500"
                    )}>{p.is_active ? "Activa" : "Pausada"}</Badge>
                    {p.new_users_only && <Badge variant="outline" className="text-xs">Solo nuevos</Badge>}
                  </div>
                  <p className="text-sm font-medium mt-0.5">{p.name}</p>
                  {p.description && <p className="text-xs text-muted-foreground line-clamp-1">{p.description}</p>}
                  <div className="flex flex-wrap items-center gap-3 mt-2 text-xs text-muted-foreground">
                    <span className="font-semibold text-blue-600 dark:text-blue-300">{fmtValue(p)}</span>
                    <span>· Vigencia: {fmtDate(p.valid_from)} — {fmtDate(p.valid_until)}</span>
                    <span>· Usos: <strong>{p.times_used || 0}</strong>{p.max_uses ? ` / ${p.max_uses}` : ""}</span>
                    <span>· Máx. por usuario: {p.max_uses_per_user}</span>
                    {usagePct !== null && (
                      <span className="inline-flex items-center gap-1">
                        <BarChart3 className="h-3 w-3" /> {usagePct}%
                      </span>
                    )}
                  </div>
                </div>
                <div className="flex gap-1 shrink-0">
                  <Button size="sm" variant="ghost" onClick={() => openEdit(p)} data-testid={`promo-edit-${p.promo_id}`}>
                    <Pencil className="h-4 w-4" />
                  </Button>
                  <Button
                    size="sm" variant="ghost"
                    onClick={() => {
                      if (window.confirm(`¿Eliminar la promoción ${p.code}? Esta acción es irreversible.`)) del.mutate(p.promo_id);
                    }}
                    data-testid={`promo-delete-${p.promo_id}`}
                    className="text-rose-500 hover:text-rose-600"
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <PromoDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        editing={editing}
      />
    </div>
  );
}

function PromoDialog({ open, onOpenChange, editing }) {
  const qc = useQueryClient();
  const [form, setForm] = useState(EMPTY);

  // Reset / prefill the form whenever the dialog opens or the target promo changes.
  // NOTE: this MUST live in an effect (not in render) — a previous version reset the
  // state during render, which caused the "code" input to appear locked because
  // every keystroke was overwritten by an EMPTY reset on the next re-render.
  useEffect(() => {
    if (!open) return;
    if (editing) {
      setForm({
        code: editing.code,
        name: editing.name,
        description: editing.description || "",
        type: editing.type,
        value: editing.value,
        currency: editing.currency || "MXN",
        duration_unit: editing.duration_unit || "months",
        valid_from: editing.valid_from ? editing.valid_from.slice(0, 10) : "",
        valid_until: editing.valid_until ? editing.valid_until.slice(0, 10) : "",
        max_uses: editing.max_uses ?? "",
        max_uses_per_user: editing.max_uses_per_user || 1,
        new_users_only: !!editing.new_users_only,
        applicable_plans: editing.applicable_plans || [],
        is_active: editing.is_active !== false,
      });
    } else {
      setForm(EMPTY);
    }
  }, [open, editing]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = useMutation({
    mutationFn: async () => {
      const body = {
        code: form.code, name: form.name, description: form.description,
        type: form.type, value: Number(form.value), currency: form.currency,
        duration_unit: form.type === "lifetime" ? form.duration_unit : null,
        valid_from: form.valid_from ? `${form.valid_from}T00:00:00+00:00` : null,
        valid_until: form.valid_until ? `${form.valid_until}T23:59:59+00:00` : null,
        max_uses: form.max_uses === "" ? null : Number(form.max_uses),
        max_uses_per_user: Number(form.max_uses_per_user) || 1,
        new_users_only: form.new_users_only,
        applicable_plans: form.applicable_plans,
        is_active: form.is_active,
      };
      const url = editing ? `/admin/promotions/${editing.promo_id}` : "/admin/promotions";
      const method = editing ? "patch" : "post";
      return (await api[method](url, body)).data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin-promotions"] });
      toast.success(editing ? "Promoción actualizada" : "Promoción creada");
      onOpenChange(false);
      setForm(EMPTY);
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "No se pudo guardar"),
  });

  const type = typeMap[form.type] || TYPES[0];

  return (
    <Dialog open={open} onOpenChange={(o) => { onOpenChange(o); if (!o) setForm(EMPTY); }}>
      <DialogContent className="glass-panel sm:max-w-lg max-h-[90vh] overflow-y-auto" data-testid="promo-dialog">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2">
            <Gift className="h-5 w-5 text-blue-500" />
            {editing ? "Editar promoción" : "Nueva promoción"}
          </DialogTitle>
          <DialogDescription>
            Configura el código, el tipo de descuento y las restricciones.
          </DialogDescription>
        </DialogHeader>

        <form
          onSubmit={(e) => { e.preventDefault(); save.mutate(); }}
          className="space-y-3"
        >
          <div>
            <label className="text-xs font-medium text-muted-foreground">Código promocional</label>
            <Input
              value={form.code}
              onChange={(e) => set("code", e.target.value.toUpperCase().replace(/\s/g, ""))}
              placeholder="Ej. NAVIDAD25"
              className="mt-1 bg-white/70 dark:bg-slate-900/40 font-mono"
              data-testid="promo-form-code"
              required
            />
          </div>
          <div>
            <label className="text-xs font-medium text-muted-foreground">Nombre interno</label>
            <Input
              value={form.name}
              onChange={(e) => set("name", e.target.value)}
              placeholder="Ej. Campaña Navidad 2026"
              className="mt-1 bg-white/70 dark:bg-slate-900/40"
              data-testid="promo-form-name"
              required
            />
          </div>
          <div>
            <label className="text-xs font-medium text-muted-foreground">Descripción (opcional)</label>
            <Textarea
              value={form.description}
              onChange={(e) => set("description", e.target.value)}
              placeholder="Ej. 25% de descuento para nuevos clientes"
              className="mt-1 bg-white/70 dark:bg-slate-900/40"
              rows={2}
              data-testid="promo-form-description"
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-medium text-muted-foreground">Tipo</label>
              <Select value={form.type} onValueChange={(v) => set("type", v)}>
                <SelectTrigger className="mt-1 bg-white/70 dark:bg-slate-900/40" data-testid="promo-form-type">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {TYPES.map((t) => (
                    <SelectItem key={t.key} value={t.key}>{t.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs font-medium text-muted-foreground">
                {form.type === "lifetime" ? "Duración" : "Valor"}
              </label>
              <Input
                type="number" min="0" step={form.type === "percent" ? "0.1" : "1"}
                value={form.value}
                onChange={(e) => set("value", e.target.value)}
                className="mt-1 bg-white/70 dark:bg-slate-900/40"
                data-testid="promo-form-value"
                disabled={form.type === "lifetime" && form.duration_unit === "forever"}
                required={!(form.type === "lifetime" && form.duration_unit === "forever")}
              />
              <p className="text-[10px] text-muted-foreground mt-0.5">{type.hint}</p>
            </div>
          </div>

          {form.type === "lifetime" && (
            <div>
              <label className="text-xs font-medium text-muted-foreground">Unidad de duración</label>
              <Select value={form.duration_unit} onValueChange={(v) => set("duration_unit", v)}>
                <SelectTrigger className="mt-1 bg-white/70 dark:bg-slate-900/40" data-testid="promo-form-duration-unit">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {DURATION_UNITS.map((u) => (
                    <SelectItem key={u.key} value={u.key}>{u.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-[10px] text-muted-foreground mt-1">
                {form.duration_unit === "forever"
                  ? "Otorga acceso ilimitado (100 años internamente)."
                  : form.duration_unit === "years"
                  ? "El valor será el número de años de acceso gratuito."
                  : "El valor será el número de meses de acceso gratuito."}
              </p>
            </div>
          )}

          {form.type === "fixed" && (
            <div>
              <label className="text-xs font-medium text-muted-foreground">Moneda</label>
              <Select value={form.currency} onValueChange={(v) => set("currency", v)}>
                <SelectTrigger className="mt-1 bg-white/70 dark:bg-slate-900/40">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="MXN">MXN</SelectItem>
                  <SelectItem value="USD">USD</SelectItem>
                </SelectContent>
              </Select>
            </div>
          )}

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-medium text-muted-foreground">Vigente desde</label>
              <Input type="date" value={form.valid_from} onChange={(e) => set("valid_from", e.target.value)} className="mt-1 bg-white/70 dark:bg-slate-900/40" data-testid="promo-form-valid-from"/>
            </div>
            <div>
              <label className="text-xs font-medium text-muted-foreground">Vigente hasta</label>
              <Input type="date" value={form.valid_until} onChange={(e) => set("valid_until", e.target.value)} className="mt-1 bg-white/70 dark:bg-slate-900/40" data-testid="promo-form-valid-until"/>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-medium text-muted-foreground">Máx. usos totales</label>
              <Input
                type="number" min="0"
                value={form.max_uses}
                onChange={(e) => set("max_uses", e.target.value)}
                placeholder="Sin límite"
                className="mt-1 bg-white/70 dark:bg-slate-900/40"
                data-testid="promo-form-max-uses"
              />
            </div>
            <div>
              <label className="text-xs font-medium text-muted-foreground">Máx. por usuario</label>
              <Input
                type="number" min="1"
                value={form.max_uses_per_user}
                onChange={(e) => set("max_uses_per_user", e.target.value)}
                className="mt-1 bg-white/70 dark:bg-slate-900/40"
                data-testid="promo-form-max-per-user"
              />
            </div>
          </div>

          <label className="flex items-center gap-2 text-sm">
            <Switch checked={form.new_users_only} onCheckedChange={(v) => set("new_users_only", v)} data-testid="promo-form-new-only" />
            <span>Solo para usuarios nuevos (en trial)</span>
          </label>
          <label className="flex items-center gap-2 text-sm">
            <Switch checked={form.is_active} onCheckedChange={(v) => set("is_active", v)} data-testid="promo-form-active" />
            <span>Activa (los usuarios pueden usarla)</span>
          </label>

          <DialogFooter className="pt-2">
            <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>Cancelar</Button>
            <Button type="submit" disabled={save.isPending} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="promo-form-submit">
              {save.isPending ? "Guardando…" : (editing ? "Guardar cambios" : "Crear promoción")}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
